// @wc-ignore-file
/**
 * CR-126 stage 3: the Figma context card's data access.
 *
 * - Import / refresh go through the api-server routes (contract:
 *   shared/types/figma-context.ts). Membership is checked on the project.
 * - The import row is read under the caller's RLS (project members only). The
 *   table has COLUMN-level SELECT grants: always name the columns —
 *   `select("*")` would expand to the server-only snapshot columns and fail.
 */
import { invokeFunction } from "@/lib/functions";
import { supabase } from "@/lib/supabase";
import type {
  FigmaContextErrorBody,
  FigmaContextImportRequest,
  FigmaContextImportSuccessBody,
  FigmaContextRefreshRequest,
  FigmaContextRefreshSuccessBody,
  FigmaContextRouteErrorCode,
} from "@shared/types/figma-context";
import type { FigmaContextImport } from "@shared/types/supabase.types";

/** The card's projection of a `figma_context_imports` row. */
export type FigmaImportRow = Pick<
  FigmaContextImport,
  | "id"
  | "project_container_id"
  | "document_container_id"
  | "source_url"
  | "figma_file_key"
  | "figma_node_id"
  | "target_type"
  | "target_name"
  | "include_visuals"
  | "status"
  | "summary"
  | "last_successful_refresh_at"
  | "last_refresh_error_code"
  | "updated_at"
>;

/** Keep in step with FigmaImportRow (and never add snapshot / snapshot_digest). */
export const FIGMA_IMPORT_COLUMNS =
  "id, project_container_id, document_container_id, source_url, figma_file_key, figma_node_id, target_type, target_name, include_visuals, status, summary, last_successful_refresh_at, last_refresh_error_code, updated_at";

/** A route error code, or `network_error` when no answer came back at all. */
export type FigmaClientErrorCode = FigmaContextRouteErrorCode | "network_error";

export class FigmaContextError extends Error {
  readonly code: FigmaClientErrorCode;
  readonly retryAfterSeconds?: number;

  constructor(code: FigmaClientErrorCode, retryAfterSeconds?: number) {
    super(code);
    this.name = "FigmaContextError";
    this.code = code;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

const ROUTE_CODES = new Set<string>([
  "unsupported_figma_url",
  "invalid_request",
  "forbidden",
  "not_found",
  "live_writes_unavailable",
  "figma_not_configured",
  "figma_reconnect_required",
  "figma_forbidden",
  "figma_target_unavailable",
  "figma_rate_limited",
  "figma_import_failed",
]);

/**
 * Map an invokeFunction error to a stable code. The membership middleware
 * answers `{ error }` without a code, so its 403 is read from the status.
 */
export function toFigmaContextError(error: {
  status?: number;
  body?: unknown;
}): FigmaContextError {
  const body = error.body as Partial<FigmaContextErrorBody> | null | undefined;
  if (body && typeof body.code === "string" && ROUTE_CODES.has(body.code)) {
    return new FigmaContextError(body.code as FigmaContextRouteErrorCode, body.retryAfterSeconds);
  }
  if (error.status === undefined) return new FigmaContextError("network_error");
  if (error.status === 401 || error.status === 403) return new FigmaContextError("forbidden");
  if (error.status === 400) return new FigmaContextError("invalid_request");
  return new FigmaContextError("figma_import_failed");
}

export async function importFigmaContext(
  request: FigmaContextImportRequest,
): Promise<FigmaContextImportSuccessBody> {
  const { data, error } = await invokeFunction<FigmaContextImportSuccessBody>(
    "figma-context-import",
    { body: request },
  );
  if (error || !data) throw toFigmaContextError(error ?? {});
  return data;
}

export async function refreshFigmaContext(
  request: FigmaContextRefreshRequest,
): Promise<FigmaContextRefreshSuccessBody> {
  const { data, error } = await invokeFunction<FigmaContextRefreshSuccessBody>(
    "figma-context-refresh",
    { body: request },
  );
  if (error || !data) throw toFigmaContextError(error ?? {});
  return data;
}

/** The project's import of one target, or null when it was never imported. */
export async function getFigmaImportByTarget(
  projectId: string,
  fileKey: string,
  nodeId: string | null,
): Promise<FigmaImportRow | null> {
  const base = supabase
    .from("figma_context_imports")
    .select(FIGMA_IMPORT_COLUMNS)
    .eq("project_container_id", projectId)
    .eq("figma_file_key", fileKey);
  const query = nodeId === null ? base.is("figma_node_id", null) : base.eq("figma_node_id", nodeId);
  const { data, error } = await query.maybeSingle();
  if (error) throw new FigmaContextError("network_error");
  return (data as FigmaImportRow | null) ?? null;
}

/** The import a generated document belongs to, or null for any other document. */
export async function getFigmaImportByDocument(
  projectId: string,
  documentId: string,
): Promise<FigmaImportRow | null> {
  const { data, error } = await supabase
    .from("figma_context_imports")
    .select(FIGMA_IMPORT_COLUMNS)
    .eq("project_container_id", projectId)
    .eq("document_container_id", documentId)
    .maybeSingle();
  if (error) throw new FigmaContextError("network_error");
  return (data as FigmaImportRow | null) ?? null;
}
